import { getFilterConfigsByCategory } from "./config";
import type { FilterConfig } from "./types";

export type DiscoverTabFilterCategory = Exclude<
  FilterConfig["category"],
  "shared"
>;

const categoryByTab = {
  events: "event",
  clubs: "club",
} as const satisfies Record<string, DiscoverTabFilterCategory>;

/** The filter category of a tab, and of the list that tab shows. */
export function getTabFilterCategory(
  tab: keyof typeof categoryByTab,
): DiscoverTabFilterCategory {
  return categoryByTab[tab];
}

const filterConfigsByCategory = {
  club: getFilterConfigsByCategory("club"),
  event: getFilterConfigsByCategory("event"),
} satisfies Record<DiscoverTabFilterCategory, unknown>;

/** Extra non-filter keys that narrow listing results. */
const narrowingQueryKeys = ["q", "radius", "sort", "f", "activities"] as const;

const allDiscoverFilterQueryKeys = [
  ...filterConfigsByCategory.event,
  ...filterConfigsByCategory.club,
].map((c) => c.queryKey);

/** Strip `NEXT_PUBLIC_BASE_PATH` prefix so the result is suitable for `router.push`. */
export function pathnameAfterBase(pathname: string): string {
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH ?? "";
  if (basePath && pathname.startsWith(basePath)) {
    return pathname.slice(basePath.length) || "/";
  }
  return pathname;
}

/**
 * True when reset would change the listing URL (activity slug or narrowing query).
 * When false, the empty state is already "full area" with nothing to clear -- hide reset.
 */
export function wouldDiscoverListingResetChangeUrl(
  pathname: string,
  search: string,
): boolean {
  const afterBase = pathnameAfterBase(pathname);
  const hasActivitySlug =
    afterBase !== discoverPathWithoutActivitySlug(pathname);

  const params = new URLSearchParams(search);
  const hasNarrowingParams =
    allDiscoverFilterQueryKeys.some((key) => params.has(key)) ||
    narrowingQueryKeys.some((key) => params.has(key));

  return hasActivitySlug || hasNarrowingParams;
}

/** Strip the activity segment from `/{locale}/{location}/{activitySlug}`. */
export function discoverPathWithoutActivitySlug(pathname: string): string {
  const segments = pathnameAfterBase(pathname).split("/").filter(Boolean);
  if (segments.length >= 3) {
    return `/${segments[0]}/${segments[1]}`;
  }
  if (segments.length === 0) {
    return "/";
  }
  return `/${segments.join("/")}`;
}

/**
 * Remove query params that narrow results (filters, search, radius, bundled `f`, legacy sort).
 * Preserves e.g. `tab`, `area`, custom location params.
 */
export function resetDiscoverListingSearchParams(search: string): string {
  const params = new URLSearchParams(search);
  for (const key of allDiscoverFilterQueryKeys) {
    params.delete(key);
  }
  for (const key of narrowingQueryKeys) {
    params.delete(key);
  }
  return params.toString();
}

/** Full client navigation target for "reset filters" from the listing empty state. */
export function getDiscoverListingResetHref(
  pathname: string,
  search: string,
): string {
  const path = discoverPathWithoutActivitySlug(pathname);
  const qs = resetDiscoverListingSearchParams(search);
  return qs ? `${path}?${qs}` : path;
}

/** Whether the listing can be reset, and if so, the href to reset to. */
export function getListingResetState(
  pathname: string,
  search: string,
): { canReset: false } | { canReset: true; resetHref: string } {
  if (!wouldDiscoverListingResetChangeUrl(pathname, search)) {
    return { canReset: false };
  }
  return {
    canReset: true,
    resetHref: getDiscoverListingResetHref(pathname, search),
  };
}

/**
 * A filter is set when its URL param is not empty, even if the value is the
 * default. "Clear all" and the filters an event reports share this rule.
 */
export function isFilterSetInUrl(
  searchParams: Pick<URLSearchParams, "get">,
  config: Pick<FilterConfig, "queryKey">,
): boolean {
  const value = searchParams.get(config.queryKey);
  return value !== null && value !== "";
}

/** The tab's filters that are set in the URL, in registry order. */
export function getActiveDiscoverTabFilters(
  searchParams: Pick<URLSearchParams, "get">,
  category: DiscoverTabFilterCategory,
): FilterConfig[] {
  return filterConfigsByCategory[category].filter((config) =>
    isFilterSetInUrl(searchParams, config),
  );
}

/**
 * Whether the current URL has any quick-filter / sheet filter params for the
 * given discover tab (matches logic used by Clear All Filters).
 */
export function hasActiveDiscoverTabFilters(
  searchParams: Pick<URLSearchParams, "get">,
  category: DiscoverTabFilterCategory,
): boolean {
  return getActiveDiscoverTabFilters(searchParams, category).length > 0;
}

/** Remove all filter query keys for the tab, preserving other params. */
export function clearDiscoverTabFilters(
  router: { push: (href: string) => void },
  currentSearch: string,
  category: DiscoverTabFilterCategory,
): void {
  const configs = filterConfigsByCategory[category];
  const params = new URLSearchParams(currentSearch);

  for (const config of configs) {
    params.delete(config.queryKey);
  }

  router.push(`?${params.toString()}`);
}
